import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Theme,
  Typography,
  Box,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  ArrowBackIcon,
  ArrowForwardIcon,
  ChevronRightIcon,
  CircleCrossIcon,
  FirstPageIcon,
  HeavyCheckIcon,
  InfoIcon,
  LastPageIcon,
} from 'assets';
import { ADMIN, CLINIC_ADMIN } from 'constants/roles';

import { IFirstLoginTooltipsContext } from 'context';
import { IApiRequests, useApiRequests, useDialog, useFirstLoginTooltips, useSubscription } from 'hooks';
import { useAppDispatch, useAppSelector } from 'store';
import { getUser, selectUser } from 'store/user';
import {
  ApiUser,
  StaffItem,
  StaffResponse,
  InvitationStatus,
  InviteRequest,
  ApiUserTooltipStagePassedEnum,
} from 'types';
import { Spinner, TwoOptionsDialog, BackButton, Info, FirstLoginTooltip } from 'components';
import { InvitePopup } from './components';
import { MoreOptions } from './components/MoreOptions/MoreOptions';
import StaffPasswordResetDialog from 'components/Dialogs/StaffPasswordResetDialog';
import { FormHeader } from '../FormHeader';
import { isHipaaLink } from 'config/productProfile';

import styles from './StaffSettings.module.scss';

const rowsPerPageOptions: number[] = [10, 20, 30];

type StaffSettingsProps = {
  onBackToSettingsClick(): void;
};

export function StaffSettings({ onBackToSettingsClick }: StaffSettingsProps) {
  const { t } = useTranslation('settings');
  const { enqueueSnackbar } = useSnackbar();
  const { showDialog } = useDialog();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const user: ApiUser = useAppSelector(selectUser)!;
  const dispatch = useAppDispatch();
  const subscription = useSubscription();

  const {
    getStaff,
    invitationResend,
    invite,
    removeStaff,
    removeInvitation,
    manageLicense,
    allowDisablePayments,
    changeUserRole,
  }: IApiRequests = useApiRequests();

  const [loading, setLoading] = useState<boolean>(true);
  const [staff, setStaff] = useState<StaffResponse | null>(null);
  const [allStaffForSearch, setAllStaffForSearch] = useState<StaffItem[] | null>(null);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [searchOffset, setSearchOffset] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [isInvitePopupOpen, setIsInvitePopupOpen] = useState<boolean>(false);
  const { tooltipsState, accept }: IFirstLoginTooltipsContext = useFirstLoginTooltips();

  const tableRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState<string>('');
  const staffTooltipCompletionKey = useMemo(
    () => `staff-invite-tooltips-completed:${user?.clinicId ?? 'unknown'}:${user?.clinicUser?.id ?? 'unknown'}`,
    [user?.clinicId, user?.clinicUser?.id],
  );
  const [isStaffInviteTooltipCompleted, setIsStaffInviteTooltipCompleted] = useState<boolean>(false);

  const isAdmin: boolean = useMemo(() => {
    return user?.clinicUser.roles.some((role: string) => role === ADMIN || role === CLINIC_ADMIN) ?? false;
  }, [user]);

  const isVisibleStaffItem = (item: StaffItem): boolean => {
    // Admin is already rendered as a dedicated first row.
    // Exclude current user from API rows to prevent Admin/Provider duplication.
    if (item.clinicUserId && item.clinicUserId === user?.clinicUser.id) {
      return false;
    }

    if (item.email && user?.email && item.email.toLowerCase() === user.email.toLowerCase()) {
      return false;
    }

    return true;
  };

  const getStaffItemStableId = (item: StaffItem): string => {
    if (item.clinicUserId) {
      return `clinicUser:${item.clinicUserId}`;
    }

    if (item.invitationId) {
      return `invitation:${item.invitationId}`;
    }

    return `fallback:${item.email ?? ''}:${item.name ?? ''}:${item.roomName ?? ''}:${item.status ?? ''}`;
  };

  const dedupeStaffItems = (items: StaffItem[]): StaffItem[] => {
    const seen = new Set<string>();

    return items.filter((item) => {
      const id = getStaffItemStableId(item);
      if (seen.has(id)) {
        return false;
      }

      seen.add(id);
      return true;
    });
  };

  const applyStaffResponse = (response: StaffResponse): void => {
    setStaff({
      ...response,
      items: dedupeStaffItems(response.items),
    });
    setAllStaffForSearch(null);
  };

  const visibleStaffItems: StaffItem[] = useMemo(() => {
    if (!staff?.items?.length) {
      return [];
    }

    return staff.items.filter(isVisibleStaffItem);
  }, [staff, user]);

  useEffect(() => {
    const query = search.trim();
    if (!isAdmin || !query) {
      setAllStaffForSearch(null);
      setSearchLoading(false);
      return;
    }

    if (allStaffForSearch !== null) {
      return;
    }

    let cancelled = false;
    const fetchAllStaffForSearch = async (): Promise<void> => {
      setSearchLoading(true);
      try {
        const pageSize = 100;
        const firstPage = await getStaff({ count: pageSize, offset: 0 });
        let items = dedupeStaffItems([...firstPage.items]);
        const totalCount = firstPage.totalCount;

        while (items.length < totalCount) {
          const nextPage = await getStaff({ count: pageSize, offset: items.length });
          if (!nextPage.items.length) {
            break;
          }

          items = dedupeStaffItems([...items, ...nextPage.items]);
        }

        if (!cancelled) {
          setAllStaffForSearch(dedupeStaffItems(items.filter(isVisibleStaffItem)));
        }
      } catch (error: any) {
        if (!cancelled) {
          enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
        }
      } finally {
        if (!cancelled) {
          setSearchLoading(false);
        }
      }
    };

    fetchAllStaffForSearch();

    return () => {
      cancelled = true;
    };
  }, [search, isAdmin, allStaffForSearch, enqueueSnackbar, user]);

  const filteredStaffItems: StaffItem[] = useMemo(() => {
    const query = search.trim().toLowerCase();
    const source = query ? allStaffForSearch ?? visibleStaffItems : visibleStaffItems;

    if (!query) {
      return source;
    }

    return source.filter(
      (item) =>
        item.name?.toLowerCase().includes(query) ||
        item.preferedDisplayName?.toLowerCase().includes(query) ||
        item.email?.toLowerCase().includes(query) ||
        item.roomName?.toLowerCase().includes(query),
    );
  }, [search, allStaffForSearch, visibleStaffItems]);

  const isSearchMode = search.trim().length > 0;

  const shouldShowAdminRow = useMemo((): boolean => {
    if (!staff || staff.offset !== 0) {
      return false;
    }

    const query = search.trim().toLowerCase();
    if (!query) {
      return true;
    }

    const adminName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim().toLowerCase();
    const adminEmail = (user?.email ?? '').toLowerCase();
    const adminRoomName = (user?.clinicUser.roomName ?? '').toLowerCase();

    return (
      adminName.includes(query) ||
      adminEmail.includes(query) ||
      adminRoomName.includes(query) ||
      'admin'.includes(query)
    );
  }, [staff, search, user]);

  const showAdminRowOnCurrentPage = useMemo((): boolean => {
    if (!shouldShowAdminRow) {
      return false;
    }

    if (!isSearchMode) {
      return true;
    }

    return searchOffset === 0;
  }, [shouldShowAdminRow, isSearchMode, searchOffset]);

  const searchTotalCount = useMemo((): number => {
    if (!isSearchMode) {
      return 0;
    }

    return filteredStaffItems.length + (shouldShowAdminRow ? 1 : 0);
  }, [isSearchMode, filteredStaffItems, shouldShowAdminRow]);

  const displayedStaffItems: StaffItem[] = useMemo(() => {
    if (!isSearchMode) {
      return filteredStaffItems;
    }

    let filteredStart = searchOffset;
    let take = rowsPerPage;

    if (shouldShowAdminRow) {
      if (searchOffset === 0) {
        filteredStart = 0;
        take = Math.max(rowsPerPage - 1, 0);
      } else {
        filteredStart = Math.max(searchOffset - 1, 0);
      }
    }

    return filteredStaffItems.slice(filteredStart, filteredStart + take);
  }, [isSearchMode, filteredStaffItems, searchOffset, rowsPerPage, shouldShowAdminRow]);

  useEffect(() => {
    const completed = localStorage.getItem(staffTooltipCompletionKey) === 'true';
    setIsStaffInviteTooltipCompleted(completed);
  }, [staffTooltipCompletionKey]);

  useEffect(() => {
    if (!isMobile || loading || !tableRef.current) {
      return;
    }

    const resetScroll = (): void => {
      tableRef.current!.scrollLeft = 0;
    };

    resetScroll();
    const frameId = window.requestAnimationFrame(resetScroll);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isMobile, loading, staff?.items?.length, rowsPerPage, search]);

  const handleStaffTooltipAccept = async (): Promise<void> => {
    const isInviteStaffStage = user.tooltipStagePassed === ApiUserTooltipStagePassedEnum.InviteStaff;
    const hasStep3Target = visibleStaffItems.length > 0;

    // Step 2 -> Step 3 transition: if step 3 has no render target, complete immediately.
    if (isInviteStaffStage && tooltipsState === 1 && !hasStep3Target) {
      await accept(); // moves to internal step 3 state
      await accept(); // persists StaffManagement stage
      localStorage.setItem(staffTooltipCompletionKey, 'true');
      setIsStaffInviteTooltipCompleted(true);
      return;
    }

    const shouldPersistCompletion = isInviteStaffStage && tooltipsState === 2;
    await accept();

    if (shouldPersistCompletion) {
      localStorage.setItem(staffTooltipCompletionKey, 'true');
      setIsStaffInviteTooltipCompleted(true);
    }
  };

  useEffect(() => {
    if (
      isStaffInviteTooltipCompleted ||
      user.tooltipStagePassed !== ApiUserTooltipStagePassedEnum.InviteStaff ||
      tooltipsState !== 2
    ) {
      return;
    }

    // Fallback: if step 3 target is not rendered (e.g. no non-admin staff rows),
    // complete the flow so tooltip state does not persist across refreshes.
    if (visibleStaffItems.length === 0) {
      handleStaffTooltipAccept();
    }
  }, [isStaffInviteTooltipCompleted, user.tooltipStagePassed, tooltipsState, visibleStaffItems.length]);

  useEffect(() => {
    setSearchOffset(0);
  }, [search]);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    getStaff({ count: rowsPerPage - 1, offset: 0 })
      .then((response: StaffResponse) => {
        applyStaffResponse(response);
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [user]);

  const paginationText = useMemo(() => {
    if (!staff) return `0 ${t('staffSettings.of')} 0`;

    if (isSearchMode) {
      if (searchTotalCount === 0) {
        return `0-0 ${t('staffSettings.of')} 0`;
      }

      const pageVisibleCount = displayedStaffItems.length + (showAdminRowOnCurrentPage ? 1 : 0);
      const start = searchOffset + 1;
      const end = Math.min(searchOffset + pageVisibleCount, searchTotalCount);

      return `${start}-${end} ${t('staffSettings.of')} ${searchTotalCount}`;
    }

    const totalCount = staff.totalCount;
    if (totalCount === 0) {
      return `0-0 ${t('staffSettings.of')} 0`;
    }

    const adminRowsBeforeCurrentPage = isAdmin && staff.offset > 0 ? 1 : 0;
    const adminRowOnPage = showAdminRowOnCurrentPage ? 1 : 0;
    const pageVisibleCount = visibleStaffItems.length + adminRowOnPage;
    const start = staff.offset + 1 + adminRowsBeforeCurrentPage;
    const end = Math.min(staff.offset + pageVisibleCount + adminRowsBeforeCurrentPage, totalCount);

    return `${start}-${end} ${t('staffSettings.of')} ${totalCount}`;
  }, [
    staff,
    isSearchMode,
    searchTotalCount,
    displayedStaffItems,
    showAdminRowOnCurrentPage,
    searchOffset,
    visibleStaffItems,
    isAdmin,
    t,
  ]);

  const getInvitationStatusText = (status: InvitationStatus): string => {
    switch (status) {
      case InvitationStatus.Inactive:
        return t('staffSettings.inactive');
      case InvitationStatus.Pending:
        return t('staffSettings.pending');
      case InvitationStatus.Active:
      default:
        return t('staffSettings.active');
    }
  };

  const formatRoomLink = (roomName?: string): string => {
    const normalized = (roomName ?? '').trim().replace(/^\/+|\/+$/g, '');
    return normalized ? `/${normalized}` : '/';
  };

  const showNotEnoughtSeatsPopupIfNeed = (): boolean => {
    if ((subscription?.availableSeats ?? 0) < 1) {
      const isVia3 = !isHipaaLink;
      const modal = showDialog(TwoOptionsDialog, {
        text: (
          <>
            <InfoIcon color='primary' sx={{ fontSize: '6.1rem', margin: 'auto' }} />

            <Typography variant='body2' component='p'>
              {isVia3 ? (
                <>
                  {t('staffSettings.notEnoughSeatsLine1')}
                  <br />
                  {t('staffSettings.notEnoughSeatsVia3Line2')}
                </>
              ) : (
                <>
                  {t('staffSettings.notEnoughSeatsLine1')}
                  <br />
                  {t('staffSettings.notEnoughSeatsHipaaLine2')}
                </>
              )}
            </Typography>
          </>
        ),
        confirmText: isVia3 ? t('staffSettings.ok') : t('staffSettings.addSeat'),
        hideCancelButton: isVia3,
        onConfirm: () => {
          modal.destroy();
          if (!isVia3) {
            navigate('/settings#subscriptions');
          }
        },
      });

      return true;
    }

    return false;
  };

  const getLocalizedStaffErrorMessage = (error: any, fallbackKey: string): string => {
    const rawMessage = error?.message ?? '';
    const errorCode = error?.errorCode ?? '';
    const normalizedMessage = String(rawMessage).trim().toLowerCase();

    if (errorCode === 'ACCEPT_PAYMENTS_ADMIN_NOT_CONNECTED') {
      return t('staffSettings.acceptPaymentsAdminNotConnected');
    }

    if (normalizedMessage.includes('cannot perform operation because admin has not connected accept payments')) {
      return t('staffSettings.acceptPaymentsAdminNotConnected');
    }

    return rawMessage || t(fallbackKey);
  };

  const allowOrDisablePayments = (clinicUserId: string, allow: boolean): Promise<any> => {
    return allowDisablePayments({
      clinicUserId: clinicUserId,
      allow,
    })
      .then(() => {
        return getStaff({ count: rowsPerPage - 1, offset: staff!.offset }).then((newStaff: StaffResponse) => {
          applyStaffResponse(newStaff);
        });
      })
      .then(() => {
        return dispatch(getUser());
      });
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<string>): void => {
    setLoading(true);

    const newRowsPerPage: number = +event.target.value;

    if (isSearchMode) {
      setRowsPerPage(newRowsPerPage);
      setSearchOffset(0);
      setLoading(false);
      return;
    }

    getStaff({ count: newRowsPerPage - 1, offset: 0 })
      .then((newStaff: StaffResponse) => {
        applyStaffResponse(newStaff);
        setRowsPerPage(newRowsPerPage);
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInvitationResend = (invitationId: string): void => {
    setLoading(true);

    invitationResend(invitationId)
      .then(() => {
        return getStaff({ count: rowsPerPage - 1, offset: staff!.offset }).then((newStaff: StaffResponse) => {
          applyStaffResponse(newStaff);
        });
      })
      .then(() => subscription?.fetchSubscriptionInfo())
      .then(() => {
        enqueueSnackbar(t('staffSettings.resendSuccessful'), { variant: 'success' });
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleResetStaffPassword = (item: StaffItem): void => {
    if (!item.clinicUserId) {
      enqueueSnackbar(t('staffSettings.cannotResetPasswordPendingInvitations'), {
        variant: 'warning',
      });
      return;
    }

    const modal = showDialog(StaffPasswordResetDialog, {
      clinicUserId: item.clinicUserId,
      staffName: item.preferedDisplayName || item.name,
      onClose: () => {
        modal.destroy();
      },
    });
  };

  const handleDeactivate = (item: StaffItem): void => {
    const manageLicensePromise = (): Promise<void> => {
      setLoading(true);

      return manageLicense({
        id: item.clinicUserId ?? item.invitationId,
        isInvite: !item.clinicUserId,
        hasLicense: false,
      })
        .then(() => {
          return getStaff({ count: rowsPerPage - 1, offset: staff!.offset }).then((newStaff: StaffResponse) => {
            applyStaffResponse(newStaff);
          });
        })
        .then(() => {
          return dispatch(getUser());
        })
        .then(() => {
          enqueueSnackbar(t('staffSettings.deactivatedSuccessfully'), { variant: 'success' });
        })
        .catch((error) => {
          enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
        })
        .finally(() => {
          setLoading(false);
        });
    };

    const modal = showDialog(TwoOptionsDialog, {
      title: t('staffSettings.deactivateUpper'),
      text: (
        <Typography variant='body2' component='p'>
          {t('staffSettings.deactivateAndUnseatQuestion', { name: item.name })}
        </Typography>
      ),
      confirmText: t('staffSettings.deactivate'),
      onConfirm: () => {
        modal.destroy();
        manageLicensePromise();
      },
    });
  };

  const handleActivate = (item: StaffItem): void => {
    const isNotEnought: boolean = showNotEnoughtSeatsPopupIfNeed();
    if (isNotEnought) {
      return;
    }

    const manageLicensePromise = (): Promise<void> => {
      setLoading(true);

      return manageLicense({
        id: item.clinicUserId ?? item.invitationId,
        isInvite: !item.clinicUserId,
        hasLicense: true,
      })
        .then(() => {
          return getStaff({ count: rowsPerPage - 1, offset: staff!.offset }).then((newStaff: StaffResponse) => {
            applyStaffResponse(newStaff);
          });
        })
        .then(() => {
          return dispatch(getUser());
        })
        .then(() => {
          enqueueSnackbar(t('staffSettings.activateSuccessful'), { variant: 'success' });
        })
        .catch((error) => {
          enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
        })
        .finally(() => {
          setLoading(false);
        });
    };

    const modal = showDialog(TwoOptionsDialog, {
      title: t('staffSettings.activateUpper'),
      text: (
        <Typography variant='body2' component='p'>
          {t('staffSettings.activateAndAssignSeatQuestion', { name: item.name })}
        </Typography>
      ),
      confirmText: t('staffSettings.activate'),
      onConfirm: () => {
        modal.destroy();
        manageLicensePromise();
      },
    });
  };

  const handleAllowPayments = (item: StaffItem): void => {
    setLoading(true);

    allowOrDisablePayments(item.clinicUserId, true)
      .then(() => {
        enqueueSnackbar(t('staffSettings.allowSuccessful'), { variant: 'success' });
      })
      .catch((error) => {
        enqueueSnackbar(getLocalizedStaffErrorMessage(error, 'staffSettings.failedToUpdatePaymentAccess'), {
          variant: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDisablePayments = (item: StaffItem): void => {
    setLoading(true);

    allowOrDisablePayments(item.clinicUserId, false)
      .then(() => {
        enqueueSnackbar(t('staffSettings.disableSuccessful'), { variant: 'success' });
      })
      .catch((error) => {
        enqueueSnackbar(getLocalizedStaffErrorMessage(error, 'staffSettings.failedToUpdatePaymentAccess'), {
          variant: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePreviousPageClick = (): void => {
    if (isSearchMode) {
      if (searchOffset === 0) {
        return;
      }

      setSearchOffset(Math.max(searchOffset - rowsPerPage, 0));
      return;
    }

    if (staff!.offset === 0) {
      return;
    }

    setLoading(true);

    getStaff({
      count: staff!.offset <= rowsPerPage ? rowsPerPage - 1 : rowsPerPage,
      offset: staff!.offset <= rowsPerPage ? 0 : staff!.offset - rowsPerPage,
    })
      .then((newStaff: StaffResponse) => {
        applyStaffResponse(newStaff);
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleNextPageClick = (): void => {
    if (isSearchMode) {
      const currentPageVisible = displayedStaffItems.length + (showAdminRowOnCurrentPage ? 1 : 0);
      const currentEnd = searchOffset + currentPageVisible;
      if (currentEnd >= searchTotalCount) {
        return;
      }

      setSearchOffset(searchOffset + rowsPerPage);
      return;
    }

    const adminRowOnPage = staff!.offset === 0 ? 1 : 0;
    const currentPageVisible = visibleStaffItems.length + adminRowOnPage;
    const adminRowsBeforeCurrentPage = isAdmin && staff!.offset > 0 ? 1 : 0;
    const currentEnd = staff!.offset + currentPageVisible + adminRowsBeforeCurrentPage;
    const totalCount = staff!.totalCount;
    if (currentEnd >= totalCount) {
      return;
    }

    setLoading(true);

    getStaff({
      count: rowsPerPage,
      offset: staff!.offset === 0 ? rowsPerPage - 1 : staff!.offset + rowsPerPage,
    })
      .then((newStaff: StaffResponse) => {
        applyStaffResponse(newStaff);
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFirstPageClick = (): void => {
    if (isSearchMode) {
      if (searchOffset === 0) {
        return;
      }

      setSearchOffset(0);
      return;
    }

    if (staff!.offset === 0) {
      return;
    }

    setLoading(true);

    getStaff({ count: rowsPerPage - 1, offset: 0 })
      .then((newStaff: StaffResponse) => {
        applyStaffResponse(newStaff);
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleLastPageClick = (): void => {
    if (isSearchMode) {
      const currentPageVisible = displayedStaffItems.length + (showAdminRowOnCurrentPage ? 1 : 0);
      const currentEnd = searchOffset + currentPageVisible;
      if (currentEnd >= searchTotalCount) {
        return;
      }

      const lastPage = Math.max(Math.ceil(searchTotalCount / rowsPerPage) - 1, 0);
      setSearchOffset(lastPage * rowsPerPage);
      return;
    }

    const adminRowOnPage = staff!.offset === 0 ? 1 : 0;
    const currentPageVisible = visibleStaffItems.length + adminRowOnPage;
    const adminRowsBeforeCurrentPage = isAdmin && staff!.offset > 0 ? 1 : 0;
    const currentEnd = staff!.offset + currentPageVisible + adminRowsBeforeCurrentPage;
    const totalCount = staff!.totalCount;
    if (currentEnd >= totalCount) {
      return;
    }

    setLoading(true);

    const lastPage = Math.max(Math.ceil(totalCount / rowsPerPage) - 1, 0);
    const lastPageGlobalStart = lastPage * rowsPerPage;
    const newOffset = isAdmin ? Math.max(lastPageGlobalStart - 1, 0) : lastPageGlobalStart;

    getStaff({ count: rowsPerPage, offset: newOffset })
      .then((newStaff: StaffResponse) => {
        applyStaffResponse(newStaff);
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleOpenIvitePopup = (): void => {
    const isNotEnought: boolean = showNotEnoughtSeatsPopupIfNeed();
    if (isNotEnought) {
      return;
    }

    setIsInvitePopupOpen(true);
  };

  const handleCloseIvitePopup = (): void => {
    setIsInvitePopupOpen(false);
  };

  const handleInviteUser = (model: InviteRequest): Promise<void> => {
    return invite(model)
      .then(() => {
        enqueueSnackbar(t('staffSettings.inviteSentSuccessfully'), { variant: 'success' });
      })
      .then(() =>
        getStaff({ count: rowsPerPage - 1, offset: 0 }).then((response: StaffResponse) => {
          applyStaffResponse(response);
          setIsInvitePopupOpen(false);
        }),
      )
      .then(() => {
        return dispatch(getUser())
          .unwrap()
          .then(() => undefined);
      })
      .catch((error) => {
        enqueueSnackbar(error.message ?? t('staffSettings.failedToSendInvite'), { variant: 'error' });
      });
  };

  const handleChangeRole = (item: StaffItem): void => {
    const currentRole = item.via3UserType === 'Member' ? 'Member' : 'Staff';
    const nextRole = currentRole === 'Member' ? 'Staff' : 'Member';
    const nextRoleLabel = nextRole === 'Member' ? t('staffSettings.member') : t('staffSettings.staff');

    const modal = showDialog(TwoOptionsDialog, {
      title: t('staffSettings.changeRoleUpper'),
      text: (
        <Typography variant='body2' component='p'>
          {t('staffSettings.changeRoleQuestion', { name: item.name, role: nextRoleLabel })}
        </Typography>
      ),
      confirmText: t('staffSettings.change'),
      onConfirm: () => {
        modal.destroy();
        changeRolePromise(item, nextRole);
      },
    });
  };

  const changeRolePromise = async (item: StaffItem, nextRole: 'Staff' | 'Member'): Promise<void> => {
    if (!item.clinicUserId) {
      enqueueSnackbar(t('staffSettings.cannotChangeRolePendingInvitations'), {
        variant: 'warning',
      });
      return;
    }

    setLoading(true);

    try {
      await changeUserRole({
        clinicUserId: item.clinicUserId,
        via3UserType: nextRole,
      });

      const newStaff = await getStaff({ count: rowsPerPage - 1, offset: 0 });
      applyStaffResponse(newStaff);
      await dispatch(getUser()).unwrap();

      enqueueSnackbar(t('staffSettings.roleChangedTo', { role: nextRole }), {
        variant: 'success',
      });
    } catch (error: any) {
      enqueueSnackbar(error.message ?? t('staffSettings.failedToChangeRole'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveStaff = (item: StaffItem): void => {
    const removeStaffCallback: () => Promise<void> = item.clinicUserId
      ? () => removeStaff(item.clinicUserId)
      : () => removeInvitation(item.invitationId);

    const removeStaffPromise = (): Promise<void> => {
      setLoading(true);

      return removeStaffCallback()
        .then(() =>
          getStaff({ count: rowsPerPage - 1, offset: 0 }).then((response: StaffResponse) => {
            applyStaffResponse(response);
          }),
        )
        .then(() => {
          return dispatch(getUser())
            .unwrap()
            .then(() => undefined);
        })
        .then(() => {
          enqueueSnackbar(t('staffSettings.staffMemberRemoved'), { variant: 'success' });
        })
        .catch((error) => {
          enqueueSnackbar(error.message ?? t('staffSettings.failedToRemoveStaff'), { variant: 'error' });
        })
        .finally(() => setLoading(false));
    };

    const modal = showDialog(TwoOptionsDialog, {
      title: t('staffSettings.removeStaffUpper'),
      text: (
        <Typography variant='body2' component='p'>
          {t('staffSettings.removeStaffQuestion', { name: item.name })}
        </Typography>
      ),
      confirmText: t('staffSettings.remove'),
      onConfirm: () => {
        modal.destroy();
        removeStaffPromise();
      },
    });
  };

  return (
    <>
      {isMobile && <BackButton text={t('staffSettings.backToSettings')} onClick={onBackToSettingsClick} />}

      <div className={styles.container}>
        <FormHeader items={[{ text: t('staffSettings.staffUpper'), isSelected: true }]} />
        {loading ? (
          <Spinner small />
        ) : (
          <div className={styles.main}>
            {isAdmin ? (
              <>
                <Stack className={styles.header}>
                  {/* Top row: seats + search + invite */}
                  <Stack direction='row' alignItems='center' gap='16px' width='100%' flexWrap='wrap'>
                    {/* LEFT: Seats */}
                    <Stack className={styles.headerSeats} direction='row'>
                      <Stack>
                        <Typography variant='body2'>
                          <b>{subscription?.availableSeats ?? 0}</b> {t('staffSettings.availableSeats')}
                        </Typography>
                      </Stack>

                      <Stack direction='row' alignItems='center' className={styles.seatsInfoRow}>
                        <Typography variant='body2'>
                          <b>{subscription?.totalSeats ?? 0}</b> {t('staffSettings.totalSeats')}
                        </Typography>
                        <Info text={t('staffSettings.manageSeatsInSubscriptions')} />
                      </Stack>
                    </Stack>

                    {/* RIGHT: Search + Invite */}
                    <Stack
                      direction='row'
                      gap='12px'
                      alignItems='center'
                      sx={{ marginLeft: 'auto' }}
                      className={styles.searchActions}
                    >
                      <input
                        type='text'
                        placeholder={t('staffSettings.searchStaff')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className={styles.searchInput}
                      />

                      <FirstLoginTooltip
                        text={
                          isHipaaLink ? t('staffSettings.inviteTooltipHipaa') : t('staffSettings.inviteTooltipVia3')
                        }
                        open={user.tooltipStagePassed === ApiUserTooltipStagePassedEnum.WelcomePage}
                        onClick={accept}
                        actionText={t('staffSettings.done')}
                      >
                        <Button
                          variant='contained'
                          className={styles.inviteButton}
                          onClick={handleOpenIvitePopup}
                          sx={{ color: '#FFFFFF' }}
                        >
                          {t('staffSettings.invite')}
                        </Button>
                      </FirstLoginTooltip>

                      {searchLoading && (
                        <Typography variant='body1' className={styles.searchStatus}>
                          {t('staffSettings.searchingAllStaff')}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Stack>

                <div className={styles.table} ref={tableRef}>
                  <Grid className={styles.tableHeader}>
                    <Typography variant='subtitle2' component='span'>
                      {t('staffSettings.name')}
                    </Typography>

                    <Typography variant='subtitle2' component='span'>
                      {t('staffSettings.email')}
                    </Typography>

                    <Typography variant='subtitle2' component='span'>
                      {t('staffSettings.roomLink')}
                    </Typography>

                    <Typography variant='subtitle2' component='span'>
                      {t('staffSettings.role')}
                    </Typography>

                    <FirstLoginTooltip
                      text={t('staffSettings.seatTooltipText')}
                      open={
                        !isStaffInviteTooltipCompleted &&
                        user.tooltipStagePassed === ApiUserTooltipStagePassedEnum.InviteStaff &&
                        tooltipsState === 0 &&
                        (staff?.items?.length ?? 0) > 0
                      }
                      onClick={handleStaffTooltipAccept}
                      numberText={t('staffSettings.oneOfThree')}
                    >
                      <Typography variant='subtitle2' component='span'>
                        {t('staffSettings.seat')}
                      </Typography>
                    </FirstLoginTooltip>

                    <FirstLoginTooltip
                      text={t('staffSettings.statusTooltipText')}
                      open={
                        !isStaffInviteTooltipCompleted &&
                        user.tooltipStagePassed === ApiUserTooltipStagePassedEnum.InviteStaff &&
                        tooltipsState === 1 &&
                        (staff?.items?.length ?? 0) > 0
                      }
                      onClick={handleStaffTooltipAccept}
                      numberText={t('staffSettings.twoOfThree')}
                    >
                      <Typography variant='subtitle2' component='span'>
                        {t('staffSettings.status')}
                      </Typography>
                    </FirstLoginTooltip>
                  </Grid>

                  {showAdminRowOnCurrentPage && (
                    <Grid className={styles.tableItem}>
                      <Tooltip
                        classes={{ tooltip: styles.tooltip }}
                        title={user?.firstName + ' ' + user?.lastName}
                        placement='bottom'
                      >
                        <Typography variant='body1'>
                          {user?.firstName} {user?.lastName}
                        </Typography>
                      </Tooltip>

                      <Tooltip classes={{ tooltip: styles.tooltip }} title={user?.email} placement='bottom'>
                        <Typography variant='body1' className={styles.techLtrCell}>
                          {user?.email}
                        </Typography>
                      </Tooltip>

                      <Tooltip
                        classes={{ tooltip: styles.tooltip }}
                        title={formatRoomLink(user?.clinicUser.roomName)}
                        placement='bottom'
                      >
                        <Typography variant='body1' className={styles.techLtrCell}>
                          {formatRoomLink(user?.clinicUser.roomName)}
                        </Typography>
                      </Tooltip>

                      <Typography variant='body1'>{t('staffSettings.admin')}</Typography>

                      <HeavyCheckIcon />

                      <Typography variant='body1'>{t('staffSettings.active')}</Typography>
                    </Grid>
                  )}

                  {displayedStaffItems.map((item: StaffItem, key: number) => (
                    <Grid key={getStaffItemStableId(item)} className={styles.tableItem}>
                      <Tooltip classes={{ tooltip: styles.tooltip }} title={item.name} placement='bottom'>
                        <Typography variant='body1'>{item.name}</Typography>
                      </Tooltip>

                      <Tooltip classes={{ tooltip: styles.tooltip }} title={item.email} placement='bottom'>
                        <Typography variant='body1' className={styles.techLtrCell}>
                          {item.email}
                        </Typography>
                      </Tooltip>

                      <Tooltip
                        classes={{ tooltip: styles.tooltip }}
                        title={formatRoomLink(item.roomName)}
                        placement='bottom'
                      >
                        <Typography variant='body1' className={styles.techLtrCell}>
                          {formatRoomLink(item.roomName)}
                        </Typography>
                      </Tooltip>

                      <Typography variant='body1'>
                        {isHipaaLink
                          ? t('staffSettings.provider')
                          : item.via3UserType === 'Member'
                          ? t('staffSettings.member')
                          : t('staffSettings.staff')}
                      </Typography>

                      {item.hasLicense ? (
                        <HeavyCheckIcon className={styles.seatStatusIcon} />
                      ) : (
                        <CircleCrossIcon className={styles.seatStatusIcon} />
                      )}

                      <Typography variant='body1'>{getInvitationStatusText(item.status)}</Typography>

                      {key === 0 ? (
                        <FirstLoginTooltip
                          text={t('staffSettings.manageStaffStatusTooltip')}
                          open={
                            !isStaffInviteTooltipCompleted &&
                            user.tooltipStagePassed === ApiUserTooltipStagePassedEnum.InviteStaff &&
                            tooltipsState === 2 &&
                            (staff?.items?.length ?? 0) > 0
                          }
                          onClick={handleStaffTooltipAccept}
                          numberText={t('staffSettings.threeOfThree')}
                          actionText={t('staffSettings.done')}
                        >
                          <div>
                            <MoreOptions
                              className={styles.moreOptionsButton}
                              item={item}
                              onActivate={handleActivate}
                              onDeactivate={handleDeactivate}
                              onRemoveStaff={handleRemoveStaff}
                              onAllowPayments={handleAllowPayments}
                              onDisablePayments={handleDisablePayments}
                              onResend={handleInvitationResend}
                              onChangeRole={handleChangeRole}
                              onResetPassword={handleResetStaffPassword}
                            />
                          </div>
                        </FirstLoginTooltip>
                      ) : (
                        <MoreOptions
                          className={styles.moreOptionsButton}
                          item={item}
                          onActivate={handleActivate}
                          onDeactivate={handleDeactivate}
                          onRemoveStaff={handleRemoveStaff}
                          onAllowPayments={handleAllowPayments}
                          onDisablePayments={handleDisablePayments}
                          onResend={handleInvitationResend}
                          onChangeRole={handleChangeRole}
                          onResetPassword={handleResetStaffPassword}
                        />
                      )}
                    </Grid>
                  ))}
                </div>

                <div className={styles.pagination}>
                  <div className={styles.rowsPerPage}>
                    <Typography variant='body1'>{t('staffSettings.rowsPerPage')}</Typography>

                    <Select
                      className={styles.select}
                      variant='outlined'
                      value={rowsPerPage.toString()}
                      onChange={handleRowsPerPageChange}
                      IconComponent={ChevronRightIcon}
                    >
                      {rowsPerPageOptions.map((option: number) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>

                  <div className={styles.paginationRight}>
                    <Typography variant='body1' className={styles.paginationText}>
                      {paginationText}
                    </Typography>

                    <Stack direction='row' className={styles.navigationButtons}>
                      <IconButton onClick={handleFirstPageClick} size='small'>
                        <FirstPageIcon />
                      </IconButton>
                      <IconButton onClick={handlePreviousPageClick} size='small'>
                        <ArrowBackIcon />
                      </IconButton>
                      <IconButton onClick={handleNextPageClick} size='small'>
                        <ArrowForwardIcon />
                      </IconButton>
                      <IconButton onClick={handleLastPageClick} size='small'>
                        <LastPageIcon />
                      </IconButton>
                    </Stack>
                  </div>
                </div>
              </>
            ) : (
              <Box className={styles.notAvailable}>
                <Typography variant='body1'>{t('staffSettings.contactAdminForStaffInfo')}</Typography>
              </Box>
            )}
          </div>
        )}
      </div>

      <InvitePopup open={isInvitePopupOpen} onClose={handleCloseIvitePopup} onInvite={handleInviteUser} />
    </>
  );
}
